import { setError } from 'js/actions';
import { CONNECTORS_WITHOUT_PATH } from 'js/Constants';
import {
  CONNECTION_CATEGORY_DATABASE,
  CONNECTION_CATEGORY_FILE,
} from 'js/constants/connectionCategories';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { getEmptyRequiredFields } from 'js/lib/ValidationUtils';
import usePaging from 'new/state/paging/repository/usePaging';
import { isUiModeCode } from 'new/utils/uiMode';
import { useCallback } from 'react';
import flowTypes from '../../../flowTypes';

export const useCDCValidations = (cdcData) => {
  const dispatch = useAppDispatch();
  const {
    connectionType,
    selectedConnection,
    connectionCategory,
    resourceConfig,
    sinkConfig,
    selectedBucket,
  } = cdcData;
  const { getSinkConfigFields } = usePaging();
  const sinkConfigFields = getSinkConfigFields(selectedConnection, flowTypes.cdc);

  const showError = (message: string) => {
    dispatch(setError({ message }));
    return false;
  };

  const validateSource = useCallback(async () => {
    // JSON/code mode: the user owns the full config in the Monaco editor, so
    // UI-field required checks (including the table selector) are intentionally
    // skipped.  Matches sink-side behavior below.
    if (isUiModeCode(resourceConfig?.resourceConfig?.source_config)) return true;

    // in manual mode, check that at least one table is selected
    const tableConfig = resourceConfig?.resourceConfig?.source_config?.['cdc.table.config'];

    if (tableConfig?.['selection.mode'] === 'manual' && !tableConfig?.['include.tables']?.length) {
      return showError('Please select at least one table.');
    }

    return true;
  }, [resourceConfig]);

  const validateSink = useCallback(async () => {
    // database sink doesn't need field validation
    if (connectionCategory === CONNECTION_CATEGORY_DATABASE) {
      return true;
    }

    // JSON/code mode: the user owns the full config in the Monaco editor, so
    // UI-field required checks and directory validation are intentionally
    // skipped.  Allow the API request to proceed.
    if (isUiModeCode(sinkConfig)) return true;

    // path: consider valid when either sinkConfig.path or selectedBucket.path
    // exists.  For Google Drive the path may live on selectedBucket before it
    // is written back into sinkConfig (e.g. right after bucket selection).
    const ignorePath = CONNECTORS_WITHOUT_PATH.includes(selectedConnection);

    if (
      connectionCategory === CONNECTION_CATEGORY_FILE &&
      !ignorePath &&
      !sinkConfig.path &&
      !selectedBucket?.path
    ) {
      return showError('Please select the destination directory.');
    }

    // required field validation
    const ignoredFields = ['start.cron'];
    const missingFields = getEmptyRequiredFields(
      sinkConfigFields?.filter((field) => !ignoredFields.includes(field.name)),
      sinkConfig,
    );

    if (missingFields.length) {
      return showError(
        'Please enter the following required fields: ' +
          missingFields.map((field) => field.display_name).join(', ') +
          '.',
      );
    }

    return true;
  }, [sinkConfig, selectedConnection, sinkConfigFields, selectedBucket, connectionCategory]);

  const validateConfig = useCallback(async () => {
    return connectionType === 'SOURCE' ? validateSource() : validateSink();
  }, [connectionType, validateSource, validateSink]);

  return { validateConfig };
};
